
import { Link } from 'react-router-dom';

export default function ArticlePartThree() {
  return (
    <div className="prose prose-lg max-w-none">
      <h2
        id="stage-4-under-contract-option-and-due-diligence-30-to-45-days"
        className="text-4xl font-bold text-black mt-16 mb-6"
      >
        Stage 4: Under Contract — Option &amp; Due Diligence (30 to 45 Days)
      </h2>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Once both parties sign the contract, your home goes &quot;under contract&quot; or &quot;pending.&quot; This is the phase where most deals either solidify or fall apart. The under‑contract period in Texas is structured around several critical milestones, each with its own deadline and potential for delay. As the seller, your primary goal during this phase is to keep the transaction moving forward while protecting your interests.
      </p>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">
        The Texas Option Period (Days 1–10)
      </h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        The option period is unique to Texas real estate transactions. For a small fee—typically $100 to $500 in El Paso—the buyer purchases the right to terminate the contract for <strong>any reason</strong> during the option period, which usually lasts seven to ten days. During this time, the buyer will schedule a professional home inspection, and the results of that inspection often determine whether the deal moves forward.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        The home inspection is the single biggest deal‑killer in El Paso real estate. A thorough inspector will examine the roof, foundation, plumbing, electrical, HVAC, and every other major system. In El Paso, common inspection findings include:
      </p>

      <ul className="list-disc pl-6 space-y-3 text-lg text-gray-700 mb-6">
        <li><strong>Foundation issues:</strong> El Paso&apos;s expansive clay soils cause foundation movement in many homes, particularly in the Lower Valley and parts of East El Paso.</li>
        <li><strong>Roof damage:</strong> Hail storms and intense UV exposure degrade roofing materials faster than in many other markets.</li>
        <li><strong>HVAC problems:</strong> Air conditioning systems work extremely hard in El Paso&apos;s 100‑degree summers and often need replacement sooner than expected.</li>
        <li><strong>Plumbing issues:</strong> Older homes in Central El Paso and the Lower Valley may have galvanized or polybutylene pipes that are nearing end of life.</li>
        <li><strong>Electrical deficiencies:</strong> Homes built before 1980 may have outdated wiring, insufficient panel capacity, or missing GFCI protection.</li>
      </ul>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        After the inspection, the buyer will typically submit an amendment requesting repairs or credits. This triggers a second round of negotiation. You can agree to the repairs, offer a credit in lieu of repairs, negotiate a reduced list of items, or refuse entirely—in which case the buyer may exercise their option to terminate. If the buyer terminates during the option period, they get their earnest money back and you are back to square one.
      </p>

      <div className="rounded-2xl overflow-hidden shadow-lg my-12 h-80">
        <img
          src="https://public.readdy.ai/ai/img_res/784e5e9ce0325c7c91593bd9e8fe2149.jpg"
          alt="Home inspector examining El Paso property during option period inspection"
          title="Under contract stage - home inspection during El Paso home selling timeline"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">
        Appraisal (Days 10–25)
      </h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        If the buyer is using a mortgage (which is the case in roughly 85 percent of El Paso transactions), their lender will order an appraisal to confirm the home&apos;s value supports the loan amount. The appraiser—an independent, licensed professional—will visit the property, measure it, photograph it, and compare it to recent comparable sales to determine its market value.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        The appraisal typically takes one to three weeks from the date it is ordered, depending on appraiser availability. In busy seasons, appraisal delays are one of the most common causes of closing postponements in El Paso. If the appraisal comes in at or above the contract price, the transaction moves forward smoothly. If it comes in <strong>below</strong> the contract price—known as a &quot;low appraisal&quot;—you have a problem.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        A low appraisal gives the buyer leverage to renegotiate the price downward, because their lender will not loan more than the appraised value. Your options include reducing the price to the appraised value, splitting the difference with the buyer, providing additional comparable sales to challenge the appraisal, or holding firm and hoping the buyer will cover the gap with additional cash. If no agreement is reached, the deal may fall through.
      </p>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">
        Buyer Financing and Loan Processing (Days 10–40)
      </h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        While the appraisal is happening, the buyer&apos;s lender is processing their mortgage application. This involves verifying income, employment, assets, credit history, and debt‑to‑income ratios. The loan underwriter reviews everything and either issues a &quot;clear to close&quot; or requests additional documentation—which can add days or weeks to the timeline.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Common financing delays in El Paso include:
      </p>

      <ul className="list-disc pl-6 space-y-3 text-lg text-gray-700 mb-6">
        <li><strong>Employment verification issues:</strong> If the buyer recently changed jobs, is self‑employed, or has irregular income, underwriting takes longer.</li>
        <li><strong>Credit problems discovered late:</strong> New debt, missed payments, or credit inquiries during the loan process can derail approval.</li>
        <li><strong>FHA/VA additional requirements:</strong> Government‑backed loans have stricter property condition requirements. If the appraiser flags issues—peeling paint, missing handrails, non‑functional appliances—the lender may require repairs before closing.</li>
        <li><strong>Down payment source verification:</strong> Large deposits or gift funds require extensive documentation.</li>
      </ul>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Financing delays are the number one reason El Paso closings get pushed back. A buyer who was pre‑approved is not the same as a buyer who is fully approved. Pre‑approval is based on preliminary information; full approval requires complete underwriting. As a seller, you have limited control over this process, which is why cash offers—which eliminate financing risk entirely—are so attractive even when the offer price is somewhat lower.
      </p>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">
        Title Work and Survey (Days 5–30)
      </h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Simultaneously, the title company is conducting a title search to verify that you have clear ownership of the property and that there are no liens, judgments, or encumbrances that would prevent the transfer. In El Paso, common title issues include:
      </p>

      <ul className="list-disc pl-6 space-y-3 text-lg text-gray-700 mb-6">
        <li>Unpaid property taxes or tax liens (El Paso&apos;s high tax rates mean delinquencies are not uncommon)</li>
        <li>Mechanic&apos;s liens from unpaid contractors</li>
        <li>HOA liens for unpaid dues</li>
        <li>Judgment liens from lawsuits</li>
        <li>Errors in prior deeds or missing signatures</li>
        <li>Probate issues on{' '}
          <Link
            to="/get-cash/inherit-house-texas"
            className="text-[#D4AF37] hover:underline font-semibold"
          >
            inherited properties
          </Link>
        </li>
      </ul>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        If title issues are discovered, they must be resolved before closing can occur. Simple issues like a small HOA lien can be cleared in days. Complex issues like probate defects or boundary disputes can take weeks or months to resolve, potentially derailing the entire transaction. Sellers who{' '}
        <Link
          to="/get-cash/selling-inherited-house-probate-texas"
          className="text-[#D4AF37] hover:underline font-semibold"
        >
          inherited a property without completing probate
        </Link>{' '}
        often discover this problem at the worst possible time—after they are already under contract.
      </p>
    </div>
  );
}
